import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';

import { makeGetMasterToken, makeGetTeamUrl, makeGetTeamToken, makeGetLoggingOut, makeGetSigningUp } from '../selectors/auth';
import { setTeamToken } from '../actions/auth';

import AjaxService from '../services/ajax-service';
import { createLoader } from '../utils';
import { createAlert } from '../services/alert-service';

import AuthFlow from '../components/auth-flow';
import AppLoading from '../components/app-loading';
import UserLinkSSO from '../components/user-link-sso';

const Main = createLoader('MainChunk', () => import(/* webpackChunkName: "MainChunk"*/ '../components/main'), <AppLoading />);

const Gate = ({ masterToken, teamUrl, teamToken, loggingOut, signingUp, router, params, children }) => {
    const [hasAccess, setHasAccess] = useState(false);
    const [loading, setLoading] = useState(true);
    const [showLogin, setShowLogin] = useState(false);
    const [showLinkSSO, setShowLinkSSO] = useState(false);

    const showAlert = (type, text) => {
        createAlert(type, text);
    }

    const showAccessAlert = reason => {
        switch (reason) {
            case 'NotFound':
                showAlert('default', 'Session expired. Please login again to access your team.');
                break;

            case 'NotJoined':
                showAlert('default', 'You have to accept your invitation first. Please check your email or contact team administrator for invitation resend.');
                break;

            case 'Removed':
                showAlert('error', 'You have been removed from that team.');
                break;

            default:
                // es lint
        }
    }

    const handleAccessError = reason => {
        setHasAccess(false);
        setLoading(false);

        if (reason) {
            if (reason === 'SSO') {
                setShowLinkSSO(true);
                return;
            }

            showAccessAlert(reason);
        }

        setShowLogin(true);
    }

    const testAccess = async (masterToken, teamUrl, teamToken) => {
        setShowLogin(false);
        setShowLinkSSO(false);

        try {
            const accessInfoResponse = await AjaxService({
                method: 'get',
                url: `teams/access/${teamUrl}`
            }, false, masterToken);

            const accessInfo = accessInfoResponse?.data;

            if (accessInfo.reason) {
                return handleAccessError(accessInfo.reason);
            }

            if (accessInfo.token !== teamToken) {
                await setTeamToken(teamUrl, accessInfo.token);
            }

            setHasAccess(Boolean(accessInfo.has_access));
            setLoading(false);
        } catch (e) {
            handleAccessError();
        }
    }

    useEffect(() => {
        if (!masterToken || !teamUrl) {
            setHasAccess(false);
            setShowLogin(true);
            setLoading(false);
            return;
        }

        testAccess(masterToken, teamUrl, teamToken);
    }, [masterToken, teamUrl]);

    if (loggingOut) {
        return null;
    }

    if (loading) {
        return <AppLoading showQuote={false} />;
    }

    if (showLinkSSO) {
        return <UserLinkSSO />;
    }

    if (showLogin) {
        return <AuthFlow />;
    }

    if (hasAccess) {
        return <Main router={router} params={params}>{children}</Main>;
    }

    return <AuthFlow />;
}

const makeMapStateToProps = () => {
    const getMasterToken = makeGetMasterToken();
    const getTeamUrl = makeGetTeamUrl();
    const getTeamToken = makeGetTeamToken();
    const getLoggingOut = makeGetLoggingOut();
    const getSigningUp = makeGetSigningUp();

    return ({ auth }) => ({
        masterToken: getMasterToken(auth),
        teamUrl: getTeamUrl(auth),
        teamToken: getTeamToken(auth),
        loggingOut: getLoggingOut(auth),
        signingUp: getSigningUp(auth)
    });
}

export default connect(makeMapStateToProps)(Gate);
